import { useState } from "react";
import { Button } from "@onemileauto/ui";
import { MAX_OOO_MESSAGE_LENGTH, MAX_SIGNATURE_LENGTH, type MailboxMembership } from "@onemileauto/shared-types";
import { useUpdateMailboxSettings } from "../../hooks/use-admin";

interface MailboxSettingsPanelProps {
  mailbox: MailboxMembership;
}

const FIELD_CLASS =
  "w-full resize-none rounded-md border border-border bg-cream px-3 py-2 text-[15px] leading-relaxed text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust";

/**
 * Signature + out-of-office editor for one owned mailbox (Phase 9). Only
 * rendered for mailboxes the current user owns (see admin-page.tsx) — the
 * real gate is server-side (requireMailbox("admin"), routes/mailboxes.ts),
 * this is just the matching client-side surface, same convention every
 * other admin control in this app follows.
 *
 * Both fields are genuinely per-mailbox (see Mailbox's docstring in
 * shared-types) — an owner of several mailboxes gets one of these panels
 * per mailbox, each independent, not a single shared "your signature"
 * field.
 */
export function MailboxSettingsPanel({ mailbox }: MailboxSettingsPanelProps) {
  const [signature, setSignature] = useState(mailbox.signature ?? "");
  const [oooEnabled, setOooEnabled] = useState(mailbox.oooEnabled);
  const [oooMessage, setOooMessage] = useState(mailbox.oooMessage ?? "");

  const update = useUpdateMailboxSettings(mailbox.id);

  const hasOooMessage = oooMessage.trim().length > 0;
  const canSave = !update.isPending && (!oooEnabled || hasOooMessage);

  function handleSave() {
    if (!canSave) return;
    update.mutate({
      signature: signature.trim() ? signature.trim() : null,
      oooEnabled,
      oooMessage: oooMessage.trim() ? oooMessage.trim() : null,
    });
  }

  return (
    <div className="border-t border-border px-4 py-4">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Signature &amp; away message</p>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted">Signature (added to new messages you write)</span>
        <textarea
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          maxLength={MAX_SIGNATURE_LENGTH}
          rows={3}
          placeholder={`Robert Byatt\nOne Mile Auto`}
          className={FIELD_CLASS}
        />
      </label>

      <div className="mt-4 rounded-md border border-dashed border-border-strong p-3">
        <label className="flex h-11 cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={oooEnabled}
            onChange={(e) => setOooEnabled(e.target.checked)}
            className="h-5 w-5 shrink-0 accent-rust"
          />
          <span className="text-[15px] font-medium text-charcoal">Automatically reply to new messages</span>
        </label>

        <label className="mt-2 flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Away message</span>
          <textarea
            value={oooMessage}
            onChange={(e) => setOooMessage(e.target.value)}
            maxLength={MAX_OOO_MESSAGE_LENGTH}
            rows={3}
            placeholder="Thanks for reaching out — I'm away right now and will get back to you soon."
            className={FIELD_CLASS}
          />
        </label>
        {oooEnabled && !hasOooMessage && (
          <p className="mt-1.5 text-xs text-danger">Add a message above before this can be turned on.</p>
        )}
        <p className="mt-1.5 text-xs text-muted">
          Sent once to each new sender, at most once a day per sender — replies never pile up.
        </p>
      </div>

      <Button type="button" onClick={handleSave} disabled={!canSave} className="mt-3">
        {update.isPending ? "Saving…" : "Save"}
      </Button>
      {update.isError && <p className="mt-2 text-sm text-danger">Couldn&rsquo;t save. Please try again.</p>}
      {update.isSuccess && <p className="mt-2 text-sm text-success">Saved.</p>}
    </div>
  );
}
